import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Building2, ArrowLeft, LayoutDashboard, Users, Trash2, UserPlus, Pencil, FileText, Scale, BarChart3, Search, UserCircle, Menu, LogOut } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CompanySelect({ user }) {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [companyStats, setCompanyStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [isUsersDialogOpen, setIsUsersDialogOpen] = useState(false);
  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    full_name: '',
    email: '',
    password: '',
    role: 'executive_manager',
  });

  useEffect(() => {
    fetchCompanies();
    fetchUsers();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      const sorted = response.data.sort((a, b) => (a.order || 99) - (b.order || 99));
      setCompanies(sorted);
      
      // Fetch stats for each company
      const statsPromises = sorted.map(company => 
        axios.get(`${API}/stats?company_id=${company.id}`)
          .then(res => ({ companyId: company.id, stats: res.data }))
          .catch(() => ({ companyId: company.id, stats: { contracts: { total: 0 }, cases: { total: 0 } } }))
      );
      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      statsResults.forEach(({ companyId, stats }) => {
        statsMap[companyId] = stats;
      });
      
      // Fetch agreements and commitments counts for IFG and وسيط الكل
      for (const company of sorted) {
        const isIFG = company.is_main === true;
        const isWaseet = company.name_ar && company.name_ar.includes('وسيط الكل');
        
        if (isIFG || isWaseet) {
          try {
            const agreementsRes = await axios.get(`${API}/agreements?company_id=${company.id}`);
            statsMap[company.id].agreements = { total: agreementsRes.data.length };
          } catch {
            statsMap[company.id].agreements = { total: 0 };
          }
        }
        
        if (isIFG) {
          try {
            const commitmentsRes = await axios.get(`${API}/commitments?company_id=${company.id}`);
            statsMap[company.id].commitments = { total: commitmentsRes.data.length };
          } catch {
            statsMap[company.id].commitments = { total: 0 };
          }
        }
      }
      
      setCompanyStats(statsMap);
    } catch (error) {
      toast.error('فشل تحميل الشركات');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleSelectCompany = (companyId, companyName) => {
    // توجيه شركة "دور وبنيان" إلى صفحة التنفيذات
    if (companyName && companyName.includes('دور وبنيان')) {
      navigate(`/executions/${companyId}`);
    } else {
      navigate(`/legal/company/${companyId}`);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, newUser);
      toast.success('تم إضافة المستخدم بنجاح');
      setIsAddUserDialogOpen(false);
      setNewUser({
        full_name: '',
        email: '',
        password: '',
        role: 'executive_manager',
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إضافة المستخدم');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('تم حذف المستخدم بنجاح');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حذف المستخدم');
    }
  };

  const handleEditUser = (u) => {
    setEditingUser({
      id: u.id,
      full_name: u.full_name,
      email: u.email,
      role: u.role,
      password: '',
    });
    setIsUsersDialogOpen(false);
    setIsEditUserDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        full_name: editingUser.full_name,
        email: editingUser.email,
        role: editingUser.role,
      };
      if (editingUser.password) {
        updateData.password = editingUser.password;
      }
      await axios.put(`${API}/users/${editingUser.id}`, updateData);
      toast.success('تم تحديث المستخدم بنجاح');
      setIsEditUserDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تحديث المستخدم');
    }
  };

  const getRoleName = (role) => {
    return role === 'legal_manager' ? 'مدير الشؤون القانونية' : 'الرئيس التنفيذي';
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-l from-slate-800 to-slate-900 text-white py-4 px-8 shadow-lg flex-shrink-0">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_contract-portal-1/artifacts/qhuwq0xk_IFG%20logo.png"
              alt="IFG Logo"
              className="h-10"
            />
            <div>
              <h1 className="text-xl font-bold">مرحباً، {user?.full_name}</h1>
              <p className="text-slate-300 text-xs">اختر الشركة للوصول إلى ملفاتها</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Dropdown Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="bg-transparent border-[#E94B7B]/50 text-[#E94B7B] hover:bg-[#E94B7B]/10 h-9 w-9 p-0"
                  data-testid="menu-btn"
                >
                  <Menu size={20} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48" dir="rtl">
                <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                  <UserCircle size={16} className="ml-2" />
                  الملف الشخصي
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/search')} className="cursor-pointer">
                  <Search size={16} className="ml-2" />
                  بحث
                </DropdownMenuItem>
                {/* التقارير - للمدير القانوني فقط */}
                {user?.role === 'legal_manager' && (
                  <DropdownMenuItem onClick={() => navigate('/reports')} className="cursor-pointer">
                    <BarChart3 size={16} className="ml-2" />
                    التقارير
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setIsUsersDialogOpen(true)} className="cursor-pointer">
                  <Users size={16} className="ml-2" />
                  إدارة المستخدمين
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => {
                    localStorage.removeItem('token');
                    navigate('/');
                  }} 
                  className="cursor-pointer text-red-600"
                >
                  <LogOut size={16} className="ml-2" />
                  تسجيل الخروج
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Users Management Dialog */}
      <Dialog open={isUsersDialogOpen} onOpenChange={setIsUsersDialogOpen}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
                  <DialogTitle className="text-2xl text-right">إدارة المستخدمين</DialogTitle>
                  <DialogDescription className="text-right">
                    قائمة المستخدمين المسجلين في النظام
                  </DialogDescription>
                </DialogHeader>
                <div className="mt-4">
                  {/* Add User Button */}
                  <div className="flex justify-end mb-4">
                    <Button
                      onClick={() => {
                        setIsUsersDialogOpen(false);
                        setIsAddUserDialogOpen(true);
                      }}
                      className="bg-[#E94B7B] hover:bg-[#d43a6a] text-white"
                      data-testid="add-user-btn"
                    >
                      <UserPlus size={16} className="ml-2" />
                      إضافة مستخدم جديد
                    </Button>
                  </div>
                  
                  {/* Users List */}
                  <div className="max-h-[400px] overflow-y-auto">
                    {users.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Users size={48} className="mx-auto mb-4 opacity-50" />
                        <p>لا يوجد مستخدمين</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {users.map((u) => (
                          <div
                            key={u.id}
                            className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                            data-testid={`user-item-${u.id}`}
                          >
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{u.full_name}</p>
                              <p className="text-sm text-slate-500">{u.email}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`text-xs px-3 py-1 rounded-full ${
                                u.role === 'legal_manager' 
                                  ? 'bg-[#E94B7B]/10 text-[#E94B7B]' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {getRoleName(u.role)}
                              </span>
                              {u.id !== user?.id && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    onClick={() => handleEditUser(u)}
                                    data-testid={`edit-user-${u.id}`}
                                  >
                                    <Pencil size={16} />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDeleteUser(u.id)}
                                    data-testid={`delete-user-${u.id}`}
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

      {/* Add User Dialog */}
      <Dialog open={isAddUserDialogOpen} onOpenChange={setIsAddUserDialogOpen}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl text-right">إضافة مستخدم جديد</DialogTitle>
            <DialogDescription className="text-right">
              أدخل بيانات المستخدم الجديد
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="user-name" className="text-right block">الاسم الكامل *</Label>
              <Input
                id="user-name"
                value={newUser.full_name}
                onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                required
                placeholder="أدخل اسم المستخدم"
                className="text-right"
                data-testid="user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-email" className="text-right block">البريد الإلكتروني *</Label>
              <Input
                id="user-email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
                placeholder="example@ifg.com"
                className="text-left"
                dir="ltr"
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-password" className="text-right block">كلمة المرور *</Label>
              <Input
                id="user-password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
                placeholder="أدخل كلمة المرور"
                className="text-left"
                dir="ltr"
                data-testid="user-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-role" className="text-right block">الصلاحية *</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger className="w-full text-right" data-testid="user-role-select">
                  <SelectValue placeholder="اختر الصلاحية" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="legal_manager">مدير الشؤون القانونية (صلاحيات كاملة)</SelectItem>
                  <SelectItem value="executive_manager">الرئيس التنفيذي (قراءة فقط)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-[#E94B7B] hover:bg-[#d43a6a]" data-testid="submit-user-button">
                إضافة المستخدم
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddUserDialogOpen(false)}
                className="flex-1"
              >
                إلغاء
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditUserDialogOpen} onOpenChange={setIsEditUserDialogOpen}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl text-right">تعديل بيانات المستخدم</DialogTitle>
            <DialogDescription className="text-right">
              عدّل بيانات المستخدم
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <form onSubmit={handleUpdateUser} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="edit-user-name" className="text-right block">الاسم الكامل *</Label>
                <Input
                  id="edit-user-name"
                  value={editingUser.full_name}
                  onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                  required
                  placeholder="أدخل اسم المستخدم"
                  className="text-right"
                  data-testid="edit-user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-user-email" className="text-right block">البريد الإلكتروني *</Label>
                <Input
                  id="edit-user-email"
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  required
                  placeholder="example@ifg.com"
                  className="text-left"
                  dir="ltr"
                  data-testid="edit-user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-user-password" className="text-right block">كلمة المرور الجديدة (اتركها فارغة للإبقاء على القديمة)</Label>
                <Input
                  id="edit-user-password"
                  type="password"
                  value={editingUser.password}
                  onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                  placeholder="أدخل كلمة المرور الجديدة"
                  className="text-left"
                  dir="ltr"
                  data-testid="edit-user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-user-role" className="text-right block">الصلاحية *</Label>
                <Select
                  value={editingUser.role}
                  onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                >
                  <SelectTrigger className="w-full text-right" data-testid="edit-user-role-select">
                    <SelectValue placeholder="اختر الصلاحية" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="legal_manager">مدير الشؤون القانونية (صلاحيات كاملة)</SelectItem>
                    <SelectItem value="executive_manager">الرئيس التنفيذي (قراءة فقط)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-[#E94B7B] hover:bg-[#d43a6a]" data-testid="update-user-button">
                  حفظ التعديلات
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsEditUserDialogOpen(false);
                    setEditingUser(null);
                  }}
                  className="flex-1"
                >
                  إلغاء
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Companies Grid */}
      <div className="flex-1 flex flex-col justify-center max-w-[1100px] mx-auto w-full px-4 py-2">
        <h2 className="text-lg font-bold text-slate-800 mb-3 text-center">
          شركات مجموعة IFG
        </h2>
        
        <div className="grid grid-cols-3 gap-3">
          {companies.map((company) => {
            const isDawrBunyan = company.name_ar && company.name_ar.includes('دور وبنيان');
            return (
            <div
              key={company.id}
              onClick={() => handleSelectCompany(company.id, company.name_ar)}
              className={`
                relative bg-white rounded-lg shadow-sm hover:shadow-lg 
                transition-all duration-300 cursor-pointer overflow-hidden
                border-2 hover:border-[#E94B7B] border-transparent
                transform hover:-translate-y-0.5 aspect-square
                ${company.is_main ? 'ring-2 ring-[#E94B7B] ring-offset-1' : ''}
              `}
              data-testid={`company-card-${company.id}`}
            >
              {company.is_main && (
                <div className="absolute top-1.5 left-1.5 bg-[#E94B7B] text-white text-[10px] px-2 py-0.5 rounded-full z-10">
                  الرئيسية
                </div>
              )}
              
              <div className="p-3 flex flex-col items-center justify-center h-full">
                {/* Logo Container */}
                <div className="w-28 h-28 mb-2 flex items-center justify-center">
                  {company.logo_url ? (
                    <img
                      src={company.logo_url}
                      alt={company.name_ar}
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <Building2 size={56} className="text-slate-300" />
                  )}
                </div>
                
                {/* Company Name */}
                <h3 className="text-base font-medium text-slate-700 text-center mb-2 line-clamp-1">
                  {company.name_ar}
                </h3>
                
                {/* Stats Counters */}
                <div className="flex flex-nowrap gap-1.5 mb-2 w-full justify-center overflow-x-auto">
                  {isDawrBunyan ? (
                    <div className="flex items-center gap-1 bg-red-50 px-2 py-1 rounded-md" data-testid={`execution-count-${company.id}`}>
                      <Scale size={14} className="text-red-600" />
                      <span className="text-sm font-semibold text-red-700">
                        {companyStats[company.id]?.executions?.total || 0}
                      </span>
                      <span className="text-xs text-red-600">تنفيذ</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded-md" data-testid={`contracts-count-${company.id}`}>
                        <FileText size={12} className="text-blue-600" />
                        <span className="text-sm font-semibold text-blue-700">
                          {companyStats[company.id]?.contracts?.total || 0}
                        </span>
                        <span className="text-xs text-blue-600">عقد</span>
                      </div>
                      <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-md" data-testid={`cases-count-${company.id}`}>
                        <Scale size={12} className="text-amber-600" />
                        <span className="text-sm font-semibold text-amber-700">
                          {companyStats[company.id]?.cases?.total || 0}
                        </span>
                        <span className="text-xs text-amber-600">قضية</span>
                      </div>
                      {/* عداد الاتفاقيات - IFG و وسيط الكل */}
                      {(company.is_main || (company.name_ar && company.name_ar.includes('وسيط الكل'))) && (
                        <div className="flex items-center gap-1 bg-indigo-50 px-2 py-1 rounded-md" data-testid={`agreements-count-${company.id}`}>
                          <FileText size={12} className="text-indigo-600" />
                          <span className="text-sm font-semibold text-indigo-700">
                            {companyStats[company.id]?.agreements?.total || 0}
                          </span>
                          <span className="text-xs text-indigo-600">اتفاقية</span>
                        </div>
                      )}
                      {/* عداد الإقرارات - IFG فقط */}
                      {company.is_main && (
                        <div className="flex items-center gap-1 bg-teal-50 px-2 py-1 rounded-md" data-testid={`commitments-count-${company.id}`}>
                          <Scale size={12} className="text-teal-600" />
                          <span className="text-sm font-semibold text-teal-700">
                            {companyStats[company.id]?.commitments?.total || 0}
                          </span>
                          <span className="text-xs text-teal-600">إقرار</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
                
                {/* Action Button */}
                <Button
                  className="bg-slate-800 hover:bg-slate-700 text-white w-full h-9 text-sm"
                  data-testid={`select-company-${company.id}`}
                >
                  <ArrowLeft size={14} className="ml-1" />
                  الدخول للشركة
                </Button>
              </div>
            </div>
          )})}
        </div>
      </div>
    </div>
  );
}
